"use client";

import { ChevronDown, ChevronRight, HeartIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { memo, useCallback, useRef, useState, useTransition } from "react";
import { ReactionButton } from "@/components/emoji/reaction-button";
import { RelativeTime } from "@/components/relative-time";
import { Button } from "@/components/ui/button";
import { deleteCommentAction } from "@/lib/actions/comments";
import { toggleCommentReactionAction } from "@/lib/actions/reactions";
import { voteCommentAction } from "@/lib/actions/votes";
import { scheduleRouterRefresh } from "@/lib/client/app-router-refresh";
import { useReplyStore } from "@/lib/state/reply-store";
import { cn } from "@/lib/utils";
import type { CommentWithDetails } from "@/types/database";
import { ReplyComposer } from "./reply-composer";
import { SafeHtmlRenderer } from "./safe-html-renderer";
import UserAvatar from "./user-avatar";

interface CommentThreadProps {
	comment: CommentWithDetails;
	depth?: number;
	currentUserId?: string;
	postId?: number;
	submissionId?: number;
	editorialSlug?: string;
}

function CommentThreadComponent({
	comment,
	depth = 0,
	currentUserId,
	postId,
	submissionId,
	editorialSlug,
}: CommentThreadProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const _router = useRouter();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [repliesCollapsed, setRepliesCollapsed] = useState(false);
	const [isDeleting, startDeleteTransition] = useTransition();
	const [isPending, startVoteTransition] = useTransition();
	const [upvotes, setUpvotes] = useState(comment.upvotes ?? 0);
	const [userVote, setUserVote] = useState<-1 | 1 | null>(
		comment.user_vote ?? null,
	);
	const isActive = useReplyStore(
		useCallback((state) => state.activeCommentId === comment.id, [comment.id]),
	);
	const startReply = useReplyStore((state) => state.startReply);
	const clearReply = useReplyStore((state) => state.clearReply);

	const resolvedPostId = postId ?? comment.post_id ?? undefined;
	const resolvedSubmissionId =
		submissionId ?? comment.submission_id ?? undefined;
	const resolvedEditorialSlug =
		editorialSlug ?? comment.editorial_slug ?? undefined;
	const isReadingReply =
		resolvedPostId !== undefined || resolvedEditorialSlug !== undefined;
	const replies = comment.replies ?? [];
	const hasReplies = replies.length > 0;
	const repliesId = `comment-${comment.id}-replies`;
	const replyCountLabel = `${replies.length} ${
		replies.length === 1 ? "reply" : "replies"
	}`;

	const handleReplyToggle = useCallback(() => {
		if (isActive) {
			clearReply();
			return;
		}

		startReply({
			commentId: comment.id,
			author: comment.author?.username ?? null,
		});
		setRepliesCollapsed(false);
		containerRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});
	}, [isActive, clearReply, startReply, comment.id, comment.author?.username]);

	const handleLikeComment = useCallback(() => {
		if (!currentUserId) {
			return;
		}

		// Determine new vote value: toggle upvote (if already upvoted, remove; otherwise add upvote)
		const newUserVote = userVote === 1 ? null : 1;

		// Optimistic update for immediate feedback
		const optimisticUpvotes =
			userVote === 1 ? Math.max(0, upvotes - 1) : upvotes + 1;

		setUpvotes(optimisticUpvotes);
		setUserVote(newUserVote);

		startVoteTransition(async () => {
			const result = await voteCommentAction(comment.id, newUserVote);

			if (!result.success) {
				// Revert optimistic update on error
				setUpvotes(comment.upvotes ?? 0);
				setUserVote(comment.user_vote ?? null);
				console.error("Failed to vote:", result.error);
			} else if (result.data) {
				// Update with server response (may differ from optimistic update)
				setUpvotes(result.data.upvotes);
				setUserVote(result.data.user_vote);
				scheduleRouterRefresh();
			}
		});
	}, [
		currentUserId,
		userVote,
		upvotes,
		comment.id,
		comment.upvotes,
		comment.user_vote,
	]);

	const handleDelete = useCallback(() => {
		if (comment.is_deleted) {
			return;
		}

		const confirmed = window.confirm(
			"Delete this comment? This action cannot be undone.",
		);
		if (
			!confirmed ||
			(!resolvedPostId && !resolvedSubmissionId && !resolvedEditorialSlug)
		) {
			return;
		}

		startDeleteTransition(async () => {
			setErrorMessage(null);

			const result = await deleteCommentAction({
				commentId: comment.id,
				postId: resolvedPostId ?? undefined,
				submissionId: resolvedSubmissionId ?? undefined,
				editorialSlug: resolvedEditorialSlug ?? undefined,
			});

			if (!result.success) {
				setErrorMessage(result.error);
				return;
			}

			if (isActive) {
				clearReply();
			}

			scheduleRouterRefresh();
		});
	}, [
		comment.id,
		comment.is_deleted,
		resolvedPostId,
		resolvedSubmissionId,
		resolvedEditorialSlug,
		isActive,
		clearReply,
	]);

	// Don't render deleted comments
	if (comment.is_deleted) {
		return null;
	}

	return (
		<div
			data-comment-thread
			data-comment-id={String(comment.id)}
			className={cn(
				"max-w-full min-w-0 transition-colors",
				depth === 0 ? "space-y-2" : "space-y-1.5",
			)}
		>
			<div
				ref={containerRef}
				className={cn(
					"py-2 transition-colors",
					depth === 0 ? "md:py-3" : "py-1.5",
					isActive ? "rounded-lg bg-black/3 dark:bg-white/5" : "bg-transparent",
				)}
			>
				<div className="flex w-full min-w-0 items-start gap-2">
					<UserAvatar
						src={comment.author?.avatar_url}
						alt={comment.author?.username}
						size={24}
						className="shrink-0"
					/>
					<div className="mb-2 flex min-w-0 flex-1 flex-col items-start gap-0 text-sm text-content-secondary">
						<div className="flex items-center justify-start gap-2 pb-2">
							{comment.author?.username ? (
								<Link
									href={`/users/${comment.author.username}`}
									className="font-normal text-content-primary transition-opacity hover:opacity-65"
								>
									{comment.author.username}
								</Link>
							) : (
								<span className="font-normal text-content-tertiary">
									Unknown
								</span>
							)}
							<time
								dateTime={comment.created_at}
								className="text-xs text-content-tertiary"
							>
								<RelativeTime when={comment.created_at} />
							</time>
						</div>

						{comment.body && (
							<div
								className={cn(
									"mb-3 w-full min-w-0 max-w-full overflow-x-auto",
									"font-normal tracking-normal text-content-primary",
									isReadingReply
										? "text-base leading-[1.6]"
										: "text-sm leading-[1.5]",
									"wrap-anywhere",
									"[&_p]:mb-2 [&_p]:min-w-0 [&_p]:max-w-full",
									"[&_img]:max-w-full [&_img]:h-auto [&_img]:py-2",
									"[&_pre]:max-w-full [&_pre]:overflow-x-auto",
									"[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto",
									"[&_iframe]:max-w-full [&_video]:max-w-full",
									"[&_a]:wrap-break-word",
								)}
							>
								<SafeHtmlRenderer
									html={comment.body}
									className="min-w-0 max-w-full"
									typography={isReadingReply ? "reading" : "interface"}
								/>
							</div>
						)}

						{comment.link_url && (
							<div className="mb-3 min-w-0 max-w-full">
								<a
									href={comment.link_url}
									target="_blank"
									rel="noopener noreferrer"
									className="break-all text-sm text-blue-600 hover:text-blue-500"
								>
									{comment.link_url}
								</a>
							</div>
						)}

						{comment.media && comment.media.length > 0 && (
							<div className="mb-3 grid w-full min-w-0 max-w-full grid-cols-2 gap-2">
								{comment.media.map((media) => (
									<div key={media.id} className="relative min-w-0">
										{media.kind === "image" && media.remote_url && (
											<Image
												src={media.remote_url}
												alt="Comment media"
												className="rounded max-w-full"
												width={media.width || undefined}
												height={media.height || undefined}
												loading="lazy"
											/>
										)}
									</div>
								))}
							</div>
						)}

						{/* actions */}
						<div className="mt-0 flex items-center justify-start gap-4">
							{/* like comment button */}
							<button
								type="button"
								onClick={handleLikeComment}
								disabled={isPending || !currentUserId}
								aria-label={
									userVote === 1 ? "Unlike this comment" : "Like this comment"
								}
								aria-pressed={userVote === 1}
								className={`flex items-center gap-1.5 text-xs font-medium transition-all duration-200 ${
									userVote === 1
										? "text-red-600 dark:text-red-400"
										: "text-content-secondary hover:text-content-primary"
								} ${isPending ? "opacity-50 cursor-not-allowed" : ""}`}
							>
								<HeartIcon
									className={`h-3.5 w-3.5 transition-all duration-200 ${
										userVote === 1
											? "fill-red-600 dark:fill-red-400 scale-110"
											: "hover:scale-110"
									}`}
								/>
								<span className={userVote === 1 ? "font-semibold" : ""}>
									{upvotes > 0 ? upvotes : ""}
								</span>
							</button>

							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-auto p-0 text-xs uppercase text-content-secondary hover:text-content-primary"
								onClick={handleReplyToggle}
								disabled={isDeleting}
							>
								{isActive ? "Cancel reply" : "Reply"}
							</Button>

							<ReactionButton
								label="React to reply"
								reactionSummary={comment.reaction_summary}
								userReactions={comment.user_reactions}
								disabled={!currentUserId}
								onToggle={(emoji, hasReaction) =>
									toggleCommentReactionAction(comment.id, emoji, hasReaction)
								}
								className="-ml-2"
							/>

							{comment.author?.id === currentUserId && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="text-red-200 hover:text-red-500 uppercase p-0 h-auto text-xs"
									onClick={handleDelete}
									disabled={isDeleting}
								>
									{isDeleting ? "Deleting..." : "Delete"}
								</Button>
							)}
						</div>

						{hasReplies ? (
							<button
								type="button"
								aria-expanded={!repliesCollapsed}
								aria-controls={repliesId}
								onClick={() => setRepliesCollapsed((collapsed) => !collapsed)}
								className="mt-2 inline-flex min-h-6 items-center gap-1.5 text-xs font-medium text-content-tertiary transition-colors hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-primary/30 focus-visible:ring-offset-2"
							>
								{repliesCollapsed ? (
									<ChevronRight className="size-3.5" strokeWidth={1.75} />
								) : (
									<ChevronDown className="size-3.5" strokeWidth={1.75} />
								)}
								<span>
									{repliesCollapsed ? "Show" : "Hide"} {replyCountLabel}
								</span>
							</button>
						) : null}

						{errorMessage && (
							<p className="mt-2 text-sm text-destructive">{errorMessage}</p>
						)}
					</div>
				</div>
			</div>

			{(resolvedPostId || resolvedSubmissionId || resolvedEditorialSlug) && (
				<ReplyComposer
					postId={resolvedPostId}
					submissionId={resolvedSubmissionId}
					editorialSlug={resolvedEditorialSlug}
					commentId={comment.id}
					show={isActive}
				/>
			)}

			{hasReplies && !repliesCollapsed ? (
				<div
					id={repliesId}
					className="relative ml-5 space-y-1.5 border-l border-black/10 pl-5 dark:border-white/12"
				>
					{replies.map((reply) => (
						<div
							key={reply.id}
							className="relative before:absolute before:-left-5 before:top-5 before:h-px before:w-4 before:bg-black/10 dark:before:bg-white/12"
						>
							<CommentThread
								comment={reply}
								postId={resolvedPostId}
								submissionId={resolvedSubmissionId}
								editorialSlug={resolvedEditorialSlug}
								currentUserId={currentUserId}
								depth={depth + 1}
							/>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
}

// Memoize CommentThread to prevent unnecessary re-renders
export const CommentThread = memo(CommentThreadComponent);
